import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ViewScope } from './view-scope';
import { buildViewScopeKey } from './view-scope';

export interface DashboardConfig {
  widgetsOrder: string[];
  hiddenWidgetIds: string[];
  settings?: Record<string, unknown>;
}

interface ViewDashboardsState {
  dashboardsByKey: Record<string, DashboardConfig>;
}

const initialState: ViewDashboardsState = {
  dashboardsByKey: {},
};

const viewDashboardsSlice = createSlice({
  name: 'viewDashboards',
  initialState,
  reducers: {
    setDashboardConfigForScope(
      state,
      action: PayloadAction<{ scope: ViewScope; config: DashboardConfig }>,
    ) {
      const { scope, config } = action.payload;
      const key = buildViewScopeKey(scope);
      state.dashboardsByKey[key] = config;
    },

    setWidgetsOrderForScope(
      state,
      action: PayloadAction<{ scope: ViewScope; widgetsOrder: string[] }>,
    ) {
      const { scope, widgetsOrder } = action.payload;
      const key = buildViewScopeKey(scope);
      const current = state.dashboardsByKey[key] || {
        widgetsOrder: [],
        hiddenWidgetIds: [],
      };

      state.dashboardsByKey[key] = {
        ...current,
        widgetsOrder,
      };
    },

    toggleWidgetVisibilityForScope(
      state,
      action: PayloadAction<{ scope: ViewScope; widgetId: string; isHidden: boolean }>,
    ) {
      const { scope, widgetId, isHidden } = action.payload;
      const key = buildViewScopeKey(scope);
      const current = state.dashboardsByKey[key] || {
        widgetsOrder: [],
        hiddenWidgetIds: [],
      };

      const hiddenSet = new Set(current.hiddenWidgetIds);
      if (isHidden) hiddenSet.add(widgetId);
      else hiddenSet.delete(widgetId);

      state.dashboardsByKey[key] = {
        ...current,
        hiddenWidgetIds: Array.from(hiddenSet),
      };
    },

    updateDashboardSettingsForScope(
      state,
      action: PayloadAction<{ scope: ViewScope; settingsPatch: Record<string, unknown> }>,
    ) {
      const { scope, settingsPatch } = action.payload;
      const key = buildViewScopeKey(scope);
      const current = state.dashboardsByKey[key] || {
        widgetsOrder: [],
        hiddenWidgetIds: [],
        settings: {},
      };

      state.dashboardsByKey[key] = {
        ...current,
        settings: {
          ...(current.settings || {}),
          ...settingsPatch,
        },
      };
    },

    resetDashboardForScope(state, action: PayloadAction<{ scope: ViewScope }>) {
      const { scope } = action.payload;
      const key = buildViewScopeKey(scope);
      delete state.dashboardsByKey[key];
    },
  },
});

export const { reducer: viewDashboardsReducer, actions: viewDashboardsActions } =
  viewDashboardsSlice;
